import { useState, useEffect } from 'react';
import {
  Dialog,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  Stack,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LabelIcon from '@mui/icons-material/Label';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StraightenIcon from '@mui/icons-material/Straighten';
import ArticleIcon from '@mui/icons-material/Article';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLanguage } from '../../../../utils/i18n';

const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return 'info';
    case 'on-progress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancel':
      return 'error';
    default:
      return 'default';
  }
};

const formatRupiah = (value) => {
  if (!value) return '0';
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(value);
};

export default function DetailWorkorder({ open, onClose, data, onUpdateStatus, getAllowedTransitions }) {
  const { t } = useLanguage();

  const statusLabels = {
    open: t('tireRepairScrap.open', 'Open'),
    'on-progress': t('tireRepairScrap.onProgress', 'On Progress'),
    completed: t('tireRepairScrap.completed', 'Completed'),
    cancel: t('tireRepairScrap.canceled', 'Canceled')
  };

  const [cancelNotes, setCancelNotes] = useState('');
  const [isCanceling, setIsCanceling] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completeDate, setCompleteDate] = useState(dayjs());

  useEffect(() => {
    if (data) {
      setCancelNotes(data.notes || '');
      setIsCanceling(false);
      setIsCompleting(false);

      const rDate = data.repair_date ? dayjs(data.repair_date) : dayjs();
      if (dayjs().isBefore(rDate, 'day')) {
        setCompleteDate(rDate);
      } else {
        setCompleteDate(dayjs());
      }
    }
  }, [data]);

  if (!data) return null;

  const woNumber = data.work_order_number_internal || data.work_order_number || '-';
  const serialNumber = data.serial_number || '-';
  const brandSize = `${data.tire_brand_name || data.tire_brand || '-'} • ${data.tire_size_code_internal || data.tire_size_code || '-'}`;

  // Steps for the Stepper
  const steps = [
    {
      label: t('tireRepairScrap.woCreated', 'Work Order Created'),
      description: t('tireRepairScrap.repairDataSaved', 'Repair data saved'),
      status: 'open'
    },
    {
      label: t('tireRepairScrap.onProgress', 'On Progress'),
      description: t('tireRepairScrap.internalOrVendor', 'Internal or vendor'),
      status: 'on-progress'
    },
    {
      label: t('tireRepairScrap.completed', 'Completed'),
      description: t('tireRepairScrap.tireReturnedToInventory', 'Tire returned to inventory'),
      status: 'completed'
    }
  ];

  let activeStep = 0;
  if (data.status === 'on-progress') activeStep = 1;
  else if (data.status === 'completed') activeStep = 3;
  else if (data.status === 'cancel') activeStep = -1; // hide or show canceled

  const allowedTransitions = getAllowedTransitions(data.status);
  const canComplete = allowedTransitions.includes('completed');
  const canProgress = allowedTransitions.includes('on-progress');
  const canCancel = allowedTransitions.includes('cancel');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', bgcolor: '#f4f7f9' } }}
    >
      <Box sx={{ position: 'absolute', top: 12, right: 16, zIndex: 10 }}>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container>
        {/* Left Pane (Now with white bg) */}
        <Grid
          item
          size={{ xs: 12, md: 4 }}
          sx={{ bgcolor: 'white', p: 3, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0' }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            {woNumber}
          </Typography>
          <Typography variant="h4" fontWeight="900" sx={{ mt: 0.5, mb: 0.5, color: '#0f172a' }}>
            {serialNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {brandSize}
          </Typography>

          <Box mb={4}>
            <Chip
              label={statusLabels[data.status] || data.status}
              color={getStatusColor(data.status)}
              size="small"
              sx={{ fontWeight: 'bold', borderRadius: 1 }}
            />
          </Box>

          <Divider sx={{ mb: 4 }} />

          {data.status === 'cancel' ? (
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" color="error.main" fontWeight="bold">
                {t('tireRepairScrap.woCanceledMsg', 'This Work Order has been canceled.')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                sx={{
                  '& .MuiStepConnector-line': { borderColor: '#e2e8f0', minHeight: 40 },
                  '& .MuiStepLabel-iconContainer': { paddingRight: 2 }
                }}
              >
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          color: activeStep >= index ? 'primary.main' : '#e2e8f0',
                          '&.Mui-completed': { color: 'primary.main' },
                          '&.Mui-active': { color: 'primary.main' }
                        }
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={activeStep >= index ? 'bold' : 'normal'}
                        color={activeStep >= index ? 'text.primary' : 'text.secondary'}
                      >
                        {step.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.description}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          <Box mt={4} display="flex" flexDirection="column" gap={1.5}>
            {canProgress && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 'bold', py: 1 }}
                onClick={() => onUpdateStatus(data, 'on-progress')}
              >
                {t('tireRepairScrap.startRepair', 'Start Repair')}
              </Button>
            )}
            {canComplete && !isCompleting && (
              <Button
                variant="contained"
                color="success"
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 'bold', py: 1, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                onClick={() => setIsCompleting(true)}
              >
                {t('tireRepairScrap.completeRepair', 'Complete Repair')}
              </Button>
            )}

            {canComplete && isCompleting && (
              <Box sx={{ mt: 1, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Typography variant="subtitle2" color="success.main" mb={1}>
                  {t('tireRepairScrap.completeRepair', 'Complete Repair')}
                </Typography>
                <Box mb={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={t('tireRepairScrap.completeDateReq', 'Complete Date *')}
                      format="DD/MM/YYYY"
                      minDate={data.repair_date ? dayjs(data.repair_date) : null}
                      value={completeDate}
                      onChange={(newVal) => setCompleteDate(newVal)}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small', required: true, sx: { bgcolor: 'white' } }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    onClick={() => setIsCompleting(false)}
                  >
                    {t('general.abort', 'Abort')}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    onClick={() => {
                      if (!completeDate) return;
                      onUpdateStatus(data, 'completed', null, completeDate.format('YYYY-MM-DD'));
                    }}
                    disabled={!completeDate}
                  >
                    {t('general.proceed', 'Proceed')}
                  </Button>
                </Stack>
              </Box>
            )}
            {canCancel && !isCanceling && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 'bold', py: 1 }}
                onClick={() => setIsCanceling(true)}
              >
                {t('tireRepairScrap.cancelWorkOrder', 'Cancel Work Order')}
              </Button>
            )}

            {canCancel && isCanceling && (
              <Box sx={{ mt: 1, p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
                <Typography variant="subtitle2" color="error.main" mb={1}>
                  {t('tireRepairScrap.cancelWorkOrder', 'Cancel Work Order')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  label={t('tireRepairScrap.cancellationReasonOpt', 'Cancellation Reason (Optional)')}
                  placeholder={t('tireRepairScrap.cancellationReasonPlaceholder', 'Please provide a reason for cancellation in English')}
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  sx={{ mb: 1.5, bgcolor: 'white' }}
                  helperText={t('tireRepairScrap.cancelNoteHelper', 'You can fill or change this note before canceling.')}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    onClick={() => setIsCanceling(false)}
                  >
                    {t('general.abort', 'Abort')}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    onClick={() => onUpdateStatus(data, 'cancel', cancelNotes)}
                  >
                    {t('general.proceed', 'Proceed')}
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Pane */}
        <Grid item size={{ xs: 12, md: 8 }} sx={{ p: 1 }}>
          {/* Top Info Card */}
          {/* Top Info Card */}
          <Paper elevation={0} sx={{ p: 2, mb: 1, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              {t('tireRepairScrap.unitTireInfo', 'Unit & Tire Information')}
            </Typography>
            <Grid container spacing={1}>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <LocalShippingIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('tireHistorical.licensePlate', 'License Plate')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {data.join?.vehicle_license_plate || data.license_plate || data.last_vehicle || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <LabelIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('general.type', 'Type')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.vehicle_type_name || data.join?.vehicle_type_name || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('general.merk', 'Merk')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.join?.vehicle_merk || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <CategoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('general.model', 'Model')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.join?.vehicle_model || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('general.name', 'Name')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.join?.vehicle_name || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <EventIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('masterVehicle.year', 'Year')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.join?.vehicle_year || '-'}
                </Typography>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('detailTireInventory.lastPosition', 'Last Position')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.last_position || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <WorkspacePremiumIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('specification.brand', 'Tire Brand')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.tire_brand_name || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <StraightenIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('specification.size', 'Tire Size')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.tire_size_code_internal || data.tire_size_code || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <ArticleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('specification.spec_name', 'Tire Spec')}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium">
                  {data.tire_spec_name || '-'}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {t('tireRepairScrap.casing', 'Casing')}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {t('tireRepairScrap.retread', 'Retread')} {data.count_retread || 0}/3
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Bottom Info Card */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('tireRepairScrap.repairDetails', 'Repair Details')}
              </Typography>
              <Chip
                label={`${t('tireRepairScrap.repair', 'Repair')} - ${data.repair_type || '-'}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            <Divider sx={{ mb: 1 }} />

            <Stack spacing={0.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.tireDamage', 'Tire Damage')}
                </Typography>
                {data.tire_damage_name ? (
                  <Chip label={data.tire_damage_name} size="small" color="error" variant="outlined" sx={{ bgcolor: '#fef2f2' }} />
                ) : (
                  <Typography variant="body2" fontWeight="medium">
                    -
                  </Typography>
                )}
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.handledBy', 'Handled By')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.vendor_name || '-'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.mechanicPic', 'Mechanic / PIC')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.mechanic_name || '-'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.repairDate', 'Repair Date')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.repair_date ? dayjs(data.repair_date).format('DD-MM-YYYY') : '-'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.estCompleteDate', 'Est. Complete Date')}
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.main">
                  {data.estimated_complete_date ? dayjs(data.estimated_complete_date).format('DD-MM-YYYY') : '-'}
                </Typography>
              </Box>
              {data.status === 'completed' && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {t('tireRepairScrap.completedAt', 'Completed At')}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {data.completed_date ? dayjs(data.completed_date).format('DD-MM-YYYY HH:mm') : '-'}
                  </Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.woInvoiceNo', 'WO Invoice No.')}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.work_order_number_internal || data.work_order_number || '-'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('tireRepairScrap.repairCost', 'Repair Cost')}
                </Typography>
                <Typography variant="h5" color="primary.main" fontWeight="900">
                  Rp {formatRupiah(data.cost_repair)}
                </Typography>
              </Box>

              {data.notes && (
                <>
                  <Divider sx={{ my: 1, borderColor: '#f1f5f9' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {data.notes}
                  </Typography>
                </>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Dialog>
  );
}
